import { datadogRum } from "@datadog/browser-rum";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { EHR_SYNC_OUTBOUND_GATED_MESSAGE, decideSyncToEhrAction } from "@utils/ehrActions";
import { getPayorConfig } from "@utils/payorConfig";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider } from "react-hook-form";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { Badge, BadgeProps, Button, Paragraph, Tooltip } from "@fronterahealth/frontera-ui-components";
import { Badge as BaseBadge } from "@fronterahealth/frontera-ui-components/ui/badge";

import {
  ApiOrganizationOrganizationTypeChoices,
  ApiPayorReportSubmissionStatusChoices,
  ApiReportEhrSyncChoices,
  ApiReportReportStatusChoices,
  ApiReportReportTypeChoices,
  ApiReportTemplateExportFrameworkChoices,
  EhrConnectionStatus,
  ExportTypeEnum,
  ReportStatusEnums,
  ReportStatusExtendedEnums,
  ReportType,
  useStartGoalsSyncToEhrMutation,
  useUpdateReportMutation,
} from "@api/graphql/types-and-hooks";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import { EHROrgContactDialog } from "@components/ClinicalNewReportDialog/EHROrgContactDialog";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { Layout, LayoutSubheader } from "@components/Layout";
import { MissingPage } from "@components/MissingPage/MissingPage";
import ClinicalReportExport from "@components/PDFGenerator/ClinicalReportExport";
import { ReviewDataProvider } from "@components/QaReview/components/ReviewDataProvider";
import { ReviewSessionProvider } from "@components/QaReview/components/ReviewSessionProvider";
import { ReviewViewSwitch } from "@components/QaReview/components/ReviewViewSwitch";
import { useIsQaScannerEnabled } from "@components/QaReview/hooks/useIsQaScannerEnabled";
import { useReportForm } from "@components/ReportField/useReportForm";
import { FinishReviewProvider } from "@components/ReportReview/FinishReviewProvider";
import { ReportReviewControls } from "@components/ReportReview/ReportReviewControls";
import { ReviewerBanner } from "@components/ReportReview/ReviewerBanner";
import { SendClientToFronteraEhrButton } from "@components/SendClientToFronteraEhr/SendClientToFronteraEhrButton";
import { StatusDefinitionTooltip } from "@components/StatusLegendTooltip/StatusDefinitionTooltip";
import { StatusTag } from "@components/StatusTag/StatusTag";
import { SyncConfirmDialog } from "@components/SyncConfirmDialog/SyncConfirmDialog";
import { WizardStepTracker } from "@components/analytics/WizardStepTracker";
import { convertReadableString } from "@components/forms/utils";
import {
  notifyError,
  notifyErrorPersistent,
  notifySuccess,
  notifySuccessPersistent,
} from "@components/notifications/notifications";
import { useEhrSyncLoginFlow } from "@hooks/useEhrSyncLoginFlow";
import { useInFlightOnce } from "@hooks/useInFlightOnce";
import { usePayorReportSubmission } from "@hooks/usePayorReportSubmission";
import { useTrackReportOpened } from "@hooks/useReportAnalytics";
import { useTemplateExport } from "@hooks/useTemplateExport";
import { FocusedFieldManagerProvider } from "@pages/ClinicalReportDetails/Providers/FocusedFieldProvider";
import { ReassessmentStepProgressNav } from "@pages/ClinicalReportDetails/ReportPage/Components/ReassessmentStepProgressNav";
import { useAdminData } from "@providers/AdminDataProvider";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";
import { useReport } from "@providers/ReportProvider";
import { SidebarViewProvider } from "@providers/SidebarViewProvider/SidebarViewProvider";
import { getStatusAndRedirectRouteFromReport } from "@routes/ClinicalReports/ClinicalReportsHelper";

const RA_STEP_ORDER = [
  "upload-files",
  "refreshing-data",
  "select-programming",
  "report-page",
  "programming",
  "narrative",
  "review-and-sign",
];

interface ButtonConfig {
  key: string;
  condition: boolean;
  text: string;
  onClick: () => void;
  disabled: boolean;
  isLoading?: boolean;
}

interface ClinicalReportNavigationState {
  returnToReportPage?: boolean;
  domainId?: string;
}

const ehrSyncBadgeClassName = (status: ApiReportEhrSyncChoices | null | undefined): BadgeProps["appearance"] => {
  if (status === ApiReportEhrSyncChoices.Inprogress) return "ai";
  if (status === ApiReportEhrSyncChoices.Pending) return "warn";
  return "success";
};

// Pre-existing layout complexity in this wizard root; refactor tracked separately.
// (typescript:S3776 cognitive-complexity exclusion is in sonar-project.properties.)
const ClinicalReportDetailsContent: React.FC = () => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEhrOrgContactDialogOpen, setIsEhrOrgContactDialogOpen] = useState<boolean>(false);

  const {
    isReportCompleted,
    isReviewAndSignPage,
    isReportPage,
    isProgrammingOrNarrativePage,
    isReassessment,
    isInitialAssessment,
    isProgressSummaryGenerating,
  } = useClinicalReportDetailsData();
  const { report, reportQuery } = useReport();
  const {
    isPassageHealthUser,
    isHippHealthUser,
    isCentralReachUser,
    isHiRasmusUser,
    isMotivityUser,
    isButterflyEffectsUser,
    isManualEHRSyncEnabled,
    isEhrSyncCleared,
    organizationType,
  } = useAdminData();
  // Frontera EHR exports through FDM's own vertical client, so like Passage it
  // needs no EHR login before syncing.
  const isFronteraEhrUser = organizationType === ApiOrganizationOrganizationTypeChoices.FronteraEhr;
  const { isCRDataWarehouse, isOrgUsernamePassword, ehrConnectionStatus, hasCredentials } = useEHRConnection();
  const flags = useFlags();
  const queryClient = useQueryClient();
  const { pathname, state } = useLocation();

  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  const templateId = report?.reportTemplate?.id;
  const templateName = report?.reportTemplate?.name;
  const analyticsReportType: "reassessment" | "diagnosis_builder" =
    report?.reportType === ApiReportReportTypeChoices.DiagnosisEvaluation ? "diagnosis_builder" : "reassessment";
  const reportContext = {
    report_id: reportIdParam ?? "",
    report_type: analyticsReportType,
    template_id: templateId,
    template_name: templateName,
    template_payor_type: report?.reportTemplate?.payorType ?? undefined,
    template_export_framework: report?.reportTemplate?.exportFramework ?? undefined,
  };
  useTrackReportOpened(reportContext);

  const reportId = report?.id ?? null;
  const payorType = report.reportTemplate?.payorType;
  const payorCopy = getPayorConfig(payorType);
  const {
    isPayorWithSubmissionIntegration,
    isPayorSubmissionEnabled,
    isSubmittingToPayorInProgress,
    isLoadingPayorReportSubmissionStatus,
    startPayorSubmissionWorkflow,
    payorReportSubmissionStatus,
    refetchPayorReportSubmissionStatus,
  } = usePayorReportSubmission({
    reportId,
    isReportCompleted,
    payorType,
  });

  // Track previous ehrSync status to detect changes
  const prevEhrSyncStatus = useRef<ApiReportEhrSyncChoices | null | undefined>(report?.ehrSync);

  const ehrName = isFronteraEhrUser ? "Frontera EHR" : convertReadableString(report.organization?.organizationType);
  const clientName = `${report?.reportClientDetails?.clientFirstName || "N/A"} ${report?.reportClientDetails?.clientLastName || ""}`;
  const headerName = clientName.trim();

  const refetchReportForPdf = useCallback(async (): Promise<ReportType | undefined> => {
    const result = await reportQuery.refetch();
    return result.data?.getReports?.edges?.[0]?.node as ReportType | undefined;
  }, [reportQuery]);

  const {
    generateAndUploadPdf,
    isExporting: isGeneratingPdfForSubmit,
    isExportDataReady,
  } = useTemplateExport({
    reportData: report,
    exportType: ExportTypeEnum.Pdf,
    refetchReport: refetchReportForPdf,
  });

  const runSubmitToPayor = async () => {
    // Under unified export, the TS-worker generation that ran at sign time
    // produced the canonical PDF in S3. Skip the frontend PDF upload so we
    // don't clobber that file — but only when the server PDF actually exists.
    // Falls back to frontend generation for reports signed before unified export
    // was enabled, an in-flight TS-worker (status Generating, URL not yet
    // populated), or a failed server-side export.
    const isDocxTemplater =
      report?.reportTemplate?.exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater;
    const hasServerPdf = !!report?.reportPdfFileSignedUrl;
    const isUnifiedExport = report?.reportTemplate?.unifiedExport ?? false;
    const skipFrontendPdfGeneration = isDocxTemplater && isUnifiedExport && hasServerPdf;
    if (!skipFrontendPdfGeneration) {
      try {
        await generateAndUploadPdf();
      } catch (error) {
        notifyError("Failed to generate report PDF. Please export the report as PDF before submitting.");
        datadogRum.addError(error, { context: payorCopy?.errorContexts.pdfGenerationFailed });
        return;
      }
    }
    await startPayorSubmissionWorkflow({ reportId: report.id })
      .then(async (data) => {
        if (data?.startPayorSubmissionWorkflow?.status && payorCopy) {
          notifySuccess(payorCopy.notifications.submitted);
          await refetchPayorReportSubmissionStatus();
        }
      })
      .catch((error) => {
        if (payorCopy) notifyError(payorCopy.notifications.submitFailed);
        datadogRum.addError(error, { context: payorCopy?.errorContexts.submissionFailed });
      });
  };

  // PROA-2622: the same double-click defect this ticket fixes for exports, on a
  // costlier path -- `generateAndUploadPdf` runs the same `buildAndUploadDocx`
  // that flips the report COMPLETED -> GENERATING, and a second click also
  // starts a second payor submission workflow. The Submit button's `isSubmitting`
  // is derived state, so it only disables a render too late.
  const handleSubmitToPayor = useInFlightOnce(runSubmitToPayor);

  const updateReportMutation = useUpdateReportMutation({});
  const [isEditTransitioning, setIsEditTransitioning] = useState(false);
  const { isPending } = updateReportMutation;
  // Show the loading CTA while the status flips back to Draft AND the
  // refetch lands, so the user doesn't see "Completed" with the old CTAs
  // for a beat before the page re-renders as a draft. Lower bound of ~700ms
  // smooths over a fast network round trip; upper bound capped by the
  // refetch promise.
  const isEditPending = isPending || isEditTransitioning;

  const updateReportStatus = async () => {
    setIsEditTransitioning(true);
    try {
      const result = await updateReportMutation.mutateAsync({
        reportId: report.id,
        status: ReportStatusEnums.Draft,
      });
      if (result.updateReport?.status) {
        await reportQuery?.refetch();
      }
    } finally {
      setIsEditTransitioning(false);
    }
  };

  const { isPending: ehrSyncPending, mutateAsync: startEhrSync } = useStartGoalsSyncToEhrMutation({});
  const syncGoalsToEhr = async () => {
    // Managed-onboarding gate: an uncleared tenant can't sync (the backend
    // rejects it too). The button is disabled; guarded here as defense-in-depth.
    if (!isEhrSyncCleared) return;
    startEhrSync({ reportId: report.id })
      .then(async (data) => {
        if (data?.startGoalsSyncToEhr?.status) {
          notifySuccess(`Sync Started. This treatment plan has been sent to ${ehrName}`);
          await reportQuery?.refetch();
        }
      })
      .catch((error) => {
        datadogRum.addError(error, { context: `Error Starting ${ehrName} Sync` });
        notifyError(`Error Starting ${ehrName} Sync`);
      });

    setDialogOpen(false);
  };

  // CentralReach reassessment write-back (goal/target sync only, no narrative
  // content) is enabled for any CR org, org-wide or per-provider — the backend's
  // standard write-back path is already report-type/template/credential-type
  // agnostic (INT-98). This is NOT Tricare-specific: the narrative/progress-
  // summary sync stays gated to the Tricare template on the backend regardless
  // of this flag (INT-127). Data Warehouse CR orgs are read-only.
  const isCrReassessmentSync = isReassessment && isCentralReachUser && !isCRDataWarehouse;

  // HiRasmus reassessment write-back (INT-109): the backend sends only the
  // report's new goals with their targets; goals already in HiRasmus are
  // skipped. HiRasmus is a per-clinician session, so it uses the same login
  // flow as a per-provider CR org.
  const isHiRasmusReassessmentSync = isReassessment && isHiRasmusUser;
  // Motivity reassessment write-back (INT-111): same backend scope and the
  // same per-clinician login flow as HiRasmus.
  const isMotivityReassessmentSync = isReassessment && isMotivityUser;
  const isSessionReassessmentSync = isCrReassessmentSync || isHiRasmusReassessmentSync || isMotivityReassessmentSync;

  // An org-wide CR org never triggers the login dialog (decideSyncToEhrAction
  // only yields open-sync/open-org-contact for it — a credential problem
  // surfaces through the org-contact dialog instead); a per-provider CR org can
  // also need open-login / open-login-pending-sync.
  const { isEHRLogin, dispatchSyncAction, loginDialogProps } = useEhrSyncLoginFlow({
    enabled: isSessionReassessmentSync,
    syncGoalsToEhr,
    openSyncConfirm: () => setDialogOpen(true),
    openOrgContact: () => setIsEhrOrgContactDialogOpen(true),
    ehrSync: report?.ehrSync,
    ehrConnectionStatus,
    isCRDataWarehouse,
    isOrgUsernamePassword,
  });

  // Allow retry when status is FAILURE or PENDING (workflow may have crashed before setting INPROGRESS).
  // Only block retry when actively INPROGRESS or after a successful COMPLETED sync.
  const cannotRetryEhrSync =
    report.ehrSync === ApiReportEhrSyncChoices.Inprogress || report.ehrSync === ApiReportEhrSyncChoices.Completed;

  const buttons: ButtonConfig[] = [
    {
      key: "sync-new-goals-to-ehr",
      condition:
        ((isInitialAssessment || isReassessment) &&
          (isPassageHealthUser || (isHippHealthUser && flags["hipphealth-sync"]) || isFronteraEhrUser)) ||
        isSessionReassessmentSync,
      text: isButterflyEffectsUser ? "Sync to EHR" : "Sync New Goals to EHR",
      onClick: () => {
        // CentralReach needs an active EHR session before it can write, so route
        // through decideSyncToEhrAction. For an org-wide CR org it only yields
        // open-sync (session usable → confirm) or open-org-contact (org EHR
        // unusable → tell them to contact their admin). A per-provider CR org
        // can also yield open-login / open-login-pending-sync, which is why both
        // dispatch via the shared login flow (useEhrSyncLoginFlow /
        // dispatchSyncAction) rather than handling the outcomes inline — a
        // missing or stale per-provider session triggers its own login dialog
        // first. HiRasmus and Motivity take the same per-provider path.
        // Passage/Hipp are API-key orgs and go straight to the confirm dialog.
        if (isSessionReassessmentSync) {
          if (!isEhrSyncCleared) return;
          dispatchSyncAction(
            decideSyncToEhrAction({
              isApiKeyOrg: false,
              isOrgUsernamePassword,
              hasCredentials,
              ehrConnectionStatus,
            }),
          );
          return;
        }
        setDialogOpen(true);
      },
      disabled: cannotRetryEhrSync || !report.learner?.externalId || ehrSyncPending || !isEhrSyncCleared,
    },
    {
      key: "submit-to-payor",
      condition:
        isPayorSubmissionEnabled && payorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.Approved,
      text:
        payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.NeedsRevision
          ? payorCopy?.button.resubmit ?? ""
          : payorCopy?.button.submit ?? "",
      onClick: handleSubmitToPayor,
      disabled:
        isLoadingPayorReportSubmissionStatus ||
        !isExportDataReady ||
        !!(
          payorReportSubmissionStatus &&
          payorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.Failed &&
          payorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.NeedsRevision
        ) ||
        isSubmittingToPayorInProgress ||
        isGeneratingPdfForSubmit,
      isLoading:
        isSubmittingToPayorInProgress ||
        isGeneratingPdfForSubmit ||
        !isExportDataReady ||
        payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.InProgress ||
        payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Initialized,
    },
  ];

  const getPassageButtonTooltipText = (): string => {
    if (cannotRetryEhrSync) return `This treatment plan has been sent to ${ehrName} and cannot be re-synced.`;
    else if (!report.learner?.externalId) return "The client does not have an external ID available.";
    else return "disabled";
  };

  const getPayorButtonTooltipText = (): string => {
    if (!payorCopy) return "disabled";
    if (payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Approved)
      return payorCopy.tooltips.approved;
    else if (payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Completed)
      return payorCopy.tooltips.awaitingReview;
    else if (payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.NeedsRevision)
      return payorCopy.tooltips.needsRevision;
    else if (
      payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Initialized ||
      payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.InProgress
    )
      return payorCopy.tooltips.submitting;
    else return "disabled";
  };

  const isReportQaScannerEnabled = useIsQaScannerEnabled();
  // Review and Sign puts Outline, Checkpoint and the review controls on their own row.
  const showReviewRow = !!isReviewAndSignPage && !!isReportQaScannerEnabled && !isReportCompleted;

  // log evaluation opening to datadog
  const base64DecodedId = atob(report.id);
  const databaseId = base64DecodedId.split("ReportType:")?.pop();
  datadogRum.addAction("report-opened", {
    reportId: databaseId,
    reportType: report.reportType,
    reportStatus: report.reportStatus,
  });

  // Monitor EHR sync status changes and show notifications
  useEffect(() => {
    const currentStatus = report?.ehrSync;
    const previousStatus = prevEhrSyncStatus.current;

    // Only show notification if status actually changed (not on initial render)
    if (previousStatus && currentStatus !== previousStatus) {
      if (currentStatus === ApiReportEhrSyncChoices.Completed) {
        notifySuccessPersistent(`EHR Sync completed successfully. Treatment plan synced to ${ehrName}.`);
      } else if (currentStatus === ApiReportEhrSyncChoices.Failure) {
        // Raw `error` from ehrSyncAlerts can include scraper exceptions /
        // upstream HTTP bodies / Python internals — keep it in Datadog for
        // support triage, but never render it to clinicians.
        const userMessage =
          report?.ehrSyncAlerts?.exportAlert?.userMessage || report?.ehrSyncAlerts?.importAlert?.userMessage;
        const errorMessage = report?.ehrSyncAlerts?.exportAlert?.error || report?.ehrSyncAlerts?.importAlert?.error;
        // Orgs that aren't cleared for EHR sync (ehr_sync_enabled off) only let frontera-internal
        // take EHR actions (enforce_ehr_sync_gate), so only the user-facing toast is gated for
        // everyone else; Datadog still records the failure.
        if (isEhrSyncCleared) {
          notifyErrorPersistent(
            userMessage || `We couldn't sync to ${ehrName}. Please try again. If the issue persists, contact support.`,
          );
        }
        datadogRum.addError(new Error("EHR Sync Failed"), {
          context: `EHR Sync Workflow Failure`,
          reportId: report.id,
          ehrName,
          errorMessage,
        });
      }

      // Org-level admin account: this sync polls report.ehrSync, not
      // ehr_connection, so refresh the connection when it resolves so a
      // credential failure surfaces on the top badge (and decideSyncToEhrAction)
      // without a manual refresh.
      if (
        isOrgUsernamePassword &&
        (currentStatus === ApiReportEhrSyncChoices.Completed || currentStatus === ApiReportEhrSyncChoices.Failure)
      ) {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      }
    }

    // Update the ref for next comparison
    prevEhrSyncStatus.current = currentStatus;
  }, [
    report?.ehrSync,
    report?.id,
    report?.ehrSyncAlerts,
    ehrName,
    isOrgUsernamePassword,
    queryClient,
    isEhrSyncCleared,
  ]);

  const isRefreshingDataPage = pathname.endsWith("/refreshing-data");
  const isSelectProgrammingPage = pathname.endsWith("/select-programming");
  const navigationState = state as ClinicalReportNavigationState | null;
  const shouldReturnToReportPage = isReassessment && isSelectProgrammingPage && !!navigationState?.returnToReportPage;

  const handleBackClick = () => {
    if (shouldReturnToReportPage && report?.id) {
      navigate(`/clinical-reports/reassessment/${report.id}/programming`, { replace: true });
      return;
    }

    navigate("/clinical-reports");
  };

  let { status } = getStatusAndRedirectRouteFromReport(report, {
    isReassessment,
  });
  if (isReassessment && isRefreshingDataPage) {
    status = "Refreshing data\u2026";
  } else if (isReassessment && isSelectProgrammingPage) {
    status = "Draft";
  } else if (
    (isReportPage || isProgrammingOrNarrativePage) &&
    report.reportStatus !== ApiReportReportStatusChoices.Completed &&
    report.extendedStatus !== ReportStatusExtendedEnums.ImportingClientData
  ) {
    status = "Draft";
  }

  return (
    <Layout>
      <WizardStepTracker reportContext={reportContext} stepOrder={RA_STEP_ORDER} />
      <LayoutSubheader
        className={twMerge(
          "flex flex-col justify-center bg-white border-b border-border-strong px-2",
          // overlap navigation for RA
          isReassessment && "top-0 z-40 -mt-(--nav-height) px-0 min-h-(--nav-height)",
        )}
        {...(isReassessment && {
          "data-slot": "layout-header-overlap",
        })}
      >
        <div className="relative flex w-full items-center justify-between px-2 py-2 min-h-[40px]">
          {isReassessment && (
            <button
              onClick={handleBackClick}
              className="mr-3 flex shrink-0 items-center justify-center rounded-sm p-1 hover:bg-limestone-50"
            >
              <ChevronLeftIcon className="h-4 w-4 text-text-primary" />
            </button>
          )}
          <div className="flex-1 min-w-0 mr-4">
            <Paragraph colorType="primary" displayType="loud" className="flex items-center gap-2">
              {/* The name gives way first, so the review buttons on the right are never covered. */}
              <span className="ph-no-capture truncate" title={headerName}>
                {headerName}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <StatusDefinitionTooltip status={status}>
                  <StatusTag status={status} />
                </StatusDefinitionTooltip>
                {report.reportTemplate?.name && (
                  <BaseBadge className="bg-static-fill-alpha-base text-static-text-strong">
                    <span className="truncate max-w-40" title={report.reportTemplate.name}>
                      {report.reportTemplate.name}
                    </span>
                  </BaseBadge>
                )}
              </span>
            </Paragraph>
          </div>
          {!showReviewRow && <ReportReviewControls />}
          {isReportCompleted && (
            <div className="flex items-center gap-2 shrink-0">
              {isManualEHRSyncEnabled &&
                report.ehrSync &&
                (isEhrSyncCleared || report.ehrSync !== ApiReportEhrSyncChoices.Failure) && (
                  <Badge
                    text={`EHR Sync ${convertReadableString(report.ehrSync)}`}
                    appearance={ehrSyncBadgeClassName(report.ehrSync)}
                    className="mr-5 px-3 py-2"
                  />
                )}
              {!(
                isPayorWithSubmissionIntegration &&
                payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Approved
              ) && (
                <Tooltip
                  text={payorCopy?.tooltips.awaitingReview ?? ""}
                  disabled={payorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.Completed}
                  showInfoIcon={false}
                >
                  <Button
                    text={
                      <div className="flex items-center gap-1">
                        {isEditPending ? <Loader className="h-4 w-4" /> : null}
                        Edit
                      </div>
                    }
                    disabled={
                      isEditPending || payorReportSubmissionStatus === ApiPayorReportSubmissionStatusChoices.Completed
                    }
                    appearance="secondary"
                    onClick={updateReportStatus}
                    className="ml-2 ring-green-400"
                  />
                </Tooltip>
              )}

              <ClinicalReportExport
                reportData={report}
                refetchReportData={async () => {
                  await reportQuery.refetch();
                }}
              />

              {buttons.map(({ condition, text, onClick, disabled, key, isLoading }) => {
                if (!condition) return null;

                // Managed-onboarding gate: the sync button is disabled while the
                // org isn't cleared; explain why on hover. Passage is graduated
                // (always cleared), so this only surfaces for a non-graduated org
                // like Hipp Health. The compact header can't host an inline notice,
                // so a tooltip on the wrapper (which still receives hover even
                // though the inner button is disabled) matches the passage/payor
                // pattern already used here.
                const isEhrSyncGated = key === "sync-new-goals-to-ehr" && !isEhrSyncCleared;
                const isDisabledWithTooltip = disabled && (isPassageHealthUser || isPayorWithSubmissionIntegration);
                const showTooltip = isDisabledWithTooltip || isEhrSyncGated;

                let tooltipText: string;
                if (isEhrSyncGated) {
                  tooltipText = `${EHR_SYNC_OUTBOUND_GATED_MESSAGE} Contact support to get started.`;
                } else if (isPayorWithSubmissionIntegration && key === "submit-to-payor") {
                  tooltipText = getPayorButtonTooltipText();
                } else {
                  tooltipText = getPassageButtonTooltipText();
                }

                const button = (
                  <Button
                    key={text}
                    text={
                      <div className="flex items-center gap-1">
                        {isLoading ? <Loader className="h-4 w-4" /> : null}
                        {text}
                      </div>
                    }
                    disabled={disabled}
                    appearance="primary"
                    onClick={onClick}
                    className="ring-green-400"
                  />
                );

                return (
                  <div key={text} className="inline-block ml-2">
                    {showTooltip ? (
                      <Tooltip text={tooltipText} showInfoIcon={false}>
                        {button}
                      </Tooltip>
                    ) : (
                      button
                    )}
                  </div>
                );
              })}

              <SendClientToFronteraEhrButton
                reportId={report.id}
                reportType={report.reportType}
                isReportCompleted={report.reportStatus === ApiReportReportStatusChoices.Completed}
                learnerExternalId={report.learner?.externalId}
                refetchReport={reportQuery.refetch}
              />
            </div>
          )}
          {/* 3-step Reassessment wayfinding — stepper anchors center via
                absolute positioning against this `relative` row; the Prev/
                Next buttons flow inline on the right. Replaces the old
                tab toggle + Continue-to-review pill. */}
          {isReassessment &&
            (isProgrammingOrNarrativePage || isReviewAndSignPage) &&
            !isProgressSummaryGenerating &&
            !isReportCompleted && <ReassessmentStepProgressNav report={report} />}
        </div>
        {/* Secondary row on Review: Outline, Checkpoint, Review. Lives on its own row
              so the main header doesn't get crowded with step nav AND Checkpoint controls. */}
      </LayoutSubheader>
      {showReviewRow && (
        <LayoutSubheader className={twMerge("flex flex-col justify-center bg-white border-b border-border-strong")}>
          <div className="flex items-center justify-end gap-3 border-t border-limestone-200 px-2 py-2">
            <ReviewViewSwitch />

            <ReportReviewControls />
          </div>
        </LayoutSubheader>
      )}
      <ReviewerBanner />
      <Outlet />
      {/* Never show the per-provider login dialog to an org-admin account — they
          have no per-provider credentials to enter. Guards against a race where
          the auto-open effect (inside useEhrSyncLoginFlow) runs before org
          metadata resolves and would otherwise strand the user in a login flow
          they cannot complete. */}
      {isEHRLogin && !isOrgUsernamePassword ? <EHRLoginDialog {...loginDialogProps} /> : null}
      <EHROrgContactDialog
        open={isEhrOrgContactDialogOpen}
        setOpen={setIsEhrOrgContactDialogOpen}
        invalidCredentials={ehrConnectionStatus === EhrConnectionStatus.CredentialsFailed}
      />

      <SyncConfirmDialog
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        title={`Confirm Sync to ${ehrName}`}
        confirmAction={syncGoalsToEhr}
        disabled={ehrSyncPending}
        syncOrg={ehrName}
      />
    </Layout>
  );
};

export const ClinicalReportDetails: React.FC = () => {
  const { reportSections } = useClinicalReportDetailsData();
  const { report } = useReport();
  const methods = useReportForm(reportSections ?? []);

  if (!report) return <MissingPage title="We can't find that report." />;

  return (
    <ReviewSessionProvider>
      <ReviewDataProvider>
        <SidebarViewProvider>
          <FinishReviewProvider>
            <FocusedFieldManagerProvider>
              <FormProvider {...methods}>
                <ClinicalReportDetailsContent />
              </FormProvider>
            </FocusedFieldManagerProvider>
          </FinishReviewProvider>
        </SidebarViewProvider>
      </ReviewDataProvider>
    </ReviewSessionProvider>
  );
};
